/**
 * Tap Room beer board.
 *
 * Fetches the current tap list from the Cloudflare Worker named in the
 * section's settings and renders it as a horizontally scrolling row of cards.
 */

const COLOUR_SCALE = [
  '#f9eba2',
  '#f5d938',
  '#efa125',
  '#d97625',
  '#c74827',
  '#9a3821',
  '#72131b',
  '#0b1310',
];

const PLACEHOLDER = `
  <svg class="beer-card__placeholder" viewBox="0 0 64 64" aria-hidden="true">
    <rect x="18" y="8" width="28" height="6" rx="2"></rect>
    <path d="M18 16h28v34a6 6 0 0 1-6 6H24a6 6 0 0 1-6-6z"></path>
    <circle cx="32" cy="34" r="8" fill="none" stroke="currentColor" stroke-width="2"></circle>
  </svg>
`;

function escapeHtml(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatTimestamp(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function colourDot(colour) {
  const index = Math.min(Math.max(parseInt(colour, 10) || 1, 1), COLOUR_SCALE.length) - 1;
  return `<span class="beer-card__colour" style="--beer-colour: ${COLOUR_SCALE[index]}" aria-hidden="true"></span>`;
}

function card(beer, text) {
  const serve = beer.serveStyle ? String(beer.serveStyle).toLowerCase() : '';
  const image = beer.imageUrl
    ? `<img class="beer-card__image" src="${escapeHtml(beer.imageUrl)}" alt="${escapeHtml(beer.name)}" loading="lazy">`
    : PLACEHOLDER;

  return `
    <article class="beer-card">
      <div class="beer-card__media">
        ${beer.serveStyle ? `<span class="beer-card__serve beer-card__serve--${escapeHtml(serve)}">${escapeHtml(beer.serveStyle)}</span>` : ''}
        ${image}
      </div>
      <div class="beer-card__body">
        ${beer.isGuest ? `<span class="beer-card__guest">${escapeHtml(text.guest)}</span>` : ''}
        <div class="beer-card__head">
          <h3 class="beer-card__name">${escapeHtml(beer.name)}</h3>
          ${beer.abv ? `<span class="beer-card__abv">${escapeHtml(beer.abv)}%</span>` : ''}
        </div>
        ${beer.style ? `<p class="beer-card__style">${escapeHtml(beer.style)}${colourDot(beer.colour)}</p>` : ''}
        ${beer.isGuest && beer.brewery ? `<p class="beer-card__brewery">${escapeHtml(text.guestFrom)} ${escapeHtml(beer.brewery)}</p>` : ''}
        ${beer.description ? `<p class="beer-card__description">${escapeHtml(beer.description)}</p>` : ''}
      </div>
    </article>
  `;
}

async function setUp(board) {
  const carousel = board.querySelector('[data-beer-board-carousel]');
  const section = board.closest('[data-section-type="vale-beer-board"]');
  const timestampEl = section ? section.querySelector('[data-beer-board-timestamp]') : null;
  const prev = board.querySelector('[data-beer-board-prev]');
  const next = board.querySelector('[data-beer-board-next]');
  const endpoint = board.dataset.endpoint;

  const text = {
    guest: board.dataset.textGuest || 'Guest',
    guestFrom: board.dataset.textGuestFrom || 'Guest beer from',
    updated: board.dataset.textUpdated || 'Live as of',
    empty: board.dataset.textEmpty || 'No beers currently on tap.',
    error: board.dataset.textError || 'Unable to load the beer list.',
  };

  const scrollBy = (direction) => {
    const first = carousel.querySelector('.beer-card');
    const step = first ? first.offsetWidth + 24 : 300;
    carousel.scrollBy({ left: step * direction, behavior: 'smooth' });
  };

  if (prev) prev.addEventListener('click', () => scrollBy(-1));
  if (next) next.addEventListener('click', () => scrollBy(1));

  if (!endpoint) {
    carousel.innerHTML = `<p class="beer-board__status">${escapeHtml(text.empty)}</p>`;
    return;
  }

  try {
    const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    const beers = Array.isArray(data.beers) ? data.beers : [];

    if (data.success === false || beers.length === 0) {
      carousel.innerHTML = `<p class="beer-board__status">${escapeHtml(text.empty)}</p>`;
      return;
    }

    carousel.innerHTML = beers.map((beer) => card(beer, text)).join('');

    if (timestampEl && data.lastUpdated) {
      const stamp = formatTimestamp(data.lastUpdated);
      const prefix = board.dataset.subtitlePrefix || '';
      if (stamp) {
        timestampEl.textContent = prefix ? `${prefix} · ${text.updated} ${stamp}` : `${text.updated} ${stamp}`;
      }
    }

    const overflows = carousel.scrollWidth > carousel.clientWidth + 4;
    board.classList.toggle('beer-board__carousel-wrapper--scrollable', overflows);
  } catch (error) {
    carousel.innerHTML = `<p class="beer-board__status">${escapeHtml(text.error)}</p>`;
    console.error('Beer board:', error);
  }
}

function init(root = document) {
  root.querySelectorAll('[data-beer-board]').forEach(setUp);
}

init();

document.addEventListener('shopify:section:load', (event) => init(event.target));
